import React from "react";
import { AlertCircle, Home, Loader, Store, Info, Phone, Mail, Globe, Users, Image, Factory, ShieldCheck, Link as LinkIcon } from "lucide-react";
import { DEFAULT_SETTINGS } from "./data/defaultProducts";
import { Product, Inquiry, Post, SiteSettings, InquiryItem } from "./types";
import {
  dbFetchProducts,
  dbUpsertProduct,
  dbDeleteProduct,
  dbBulkUpsertProducts,
  dbBulkDeleteProducts,
  dbFetchSiteSettings,
  dbUpsertSiteSettings,
  dbFetchInquiries,
  dbInsertInquiry,
  dbDeleteInquiry,
  dbUpdateInquiryStatus,
  dbFetchPosts,
  dbUpsertPost,
  dbDeletePost,
  getSupabaseClient,
  getOptimizedImageUrl
} from "./lib/supabase";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Catalog from "./components/Catalog";
import AlmasLanding from "./components/AlmasLanding";
// Lazy load heavy components to improve PageSpeed (LCP/TBT)
const AdminPanel = React.lazy(() => import("./components/AdminPanel"));
const Blog = React.lazy(() => import("./components/Blog"));
const About = React.lazy(() => import("./components/About"));
const Contact = React.lazy(() => import("./components/Contact"));

export default function App() {
  const getFooterIcon = (iconName?: string) => {
    const props = { className: "w-3.5 h-3.5" };
    switch (iconName) {
      case "Home": return <Home {...props} />;
      case "Store": return <Store {...props} />;
      case "Info": return <Info {...props} />;
      case "Phone": return <Phone {...props} />;
      case "Mail": return <Mail {...props} />;
      case "Globe": return <Globe {...props} />;
      case "Users": return <Users {...props} />;
      case "Instagram": return <Image {...props} />;
      case "Factory": return <Factory {...props} />;
      case "ShieldCheck": return <ShieldCheck {...props} />;
      default: return <LinkIcon {...props} />;
    }
  };
  // تشخیص هوشمند حالت اجرای اپلیکیشن (client, admin, or hybrid)
  const appMode = import.meta.env.VITE_APP_MODE || "hybrid";

  // معماری چندمستأجره (Multi-Tenant)
  const [activeTenant, setActiveTenant] = React.useState<string>(() => {
    if (appMode === "admin" && typeof sessionStorage !== "undefined") return sessionStorage.getItem("afcms_active_tenant") || "default";
    return import.meta.env.VITE_TENANT_ID || "default";
  });

  // Tabs State
  const [currentTab, setCurrentTab] = React.useState<string>(() => {
    if (appMode === "admin") return "admin";

    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      const params = new URLSearchParams(window.location.search);
      
      // Security: Block admin access if not in admin mode
      if (path === "/app" || path === "/admin") return "404";
      
      // If we have blog parameters, prioritize blog tab even on home path
      if (params.has("slug") || params.has("cat")) return "blog";
      
      if (path === "/store") return "store";
      if (path === "/about") return "about";
      if (path === "/contact") return "contact";
      if (path === "/blog") return "blog";
      if (path === "/" || path === "") return "home";
      return "404";
    }
    return "home";
  });
  const [searchQuery, setSearchQuery] = React.useState<string>(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      return params.get("q") || "";
    }
    return "";
  });
  const [selectedCategory, setSelectedCategory] = React.useState<string>(() => {
    if (typeof window !== "undefined" && appMode !== "admin") {
      const params = new URLSearchParams(window.location.search);
      return params.get("cat") || "همه";
    }
    return "همه";
  });
  const [selectedOrigin, setSelectedOrigin] = React.useState<string>("همه");
  const [sortBy, setSortBy] = React.useState<string>("default");
  const [appLanguage, setAppLanguage] = React.useState<"fa" | "en">(() => {
    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      const params = new URLSearchParams(window.location.search);
      
      // 1. Check for legacy WordPress locale paths
      if (path.includes('/en_us/')) return "en";
      if (path.includes('/da_af/')) return "fa";

      // 2. Check for current lang parameter
      const urlLang = params.get("lang");
      if (urlLang === "en" || urlLang === "fa") return urlLang;

      // 3. Fallback to LocalStorage
      const localLang = localStorage.getItem("appLanguage");
      if (localLang === "en" || localLang === "fa") return localLang as "fa" | "en";
    }
    return "fa";
  });

  // Persisted Database States
  const [products, setProducts] = React.useState<Product[]>([]);
  const [inquiries, setInquiries] = React.useState<Inquiry[]>([]);
  const [posts, setPosts] = React.useState<Post[]>([]);
  const [settings, setSettings] = React.useState<SiteSettings>(DEFAULT_SETTINGS);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = React.useState(false);
  const [isAppLoading, setIsAppLoading] = React.useState(true);

  // Sync Language to URL and LocalStorage
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      // Force "fa" if bilingual mode is explicitly disabled in settings
      if (settings.enableBilingual === false && appLanguage !== "fa") {
        setAppLanguage("fa");
        return;
      }

      localStorage.setItem("appLanguage", appLanguage);
      const url = new URL(window.location.href);
      
      // If we landed on a legacy path, strip it from the URL after detecting language
      let pathChanged = false;
      if (url.pathname.includes('/en_us/') || url.pathname.includes('/da_af/')) {
        url.pathname = url.pathname.replace('/en_us/', '/').replace('/da_af/', '/');
        pathChanged = true;
      }

      // Only sync language to URL if bilingual mode is enabled
      if (settings.enableBilingual) {
        if (url.searchParams.get("lang") !== appLanguage || pathChanged) {
          url.searchParams.set("lang", appLanguage);
          window.history.replaceState({}, "", url.toString());
        }
      } else {
        // If not bilingual, ensure lang parameter is removed from URL
        if (url.searchParams.has("lang") || pathChanged) {
          url.searchParams.delete("lang");
          window.history.replaceState({}, "", url.toString());
        }
      }
    }
  }, [appLanguage, settings.enableBilingual]);

  // Global SEO Schema Injection (Organization & WebSite)
  React.useEffect(() => {
    if (typeof document !== "undefined") {
      // Clean up old scripts first to prevent duplicates
      document.querySelectorAll('.afcms-global-schema').forEach(el => el.remove());

      const organizationJsonLd = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": appLanguage === "en" ? (settings.titleEn || settings.title) : settings.title,
        "url": window.location.origin,
        "logo": settings.logoUrl,
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": settings.contactPhone,
          "contactType": "customer service"
        }
      };

      const websiteJsonLd = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": appLanguage === "en" ? (settings.titleEn || settings.title) : settings.title,
        "url": window.location.origin,
        "potentialAction": {
          "@type": "SearchAction",
          "target": `${window.location.origin}/store?q={search_term_string}`,
          "query-input": "required name=search_term_string"
        }
      };

      const scriptOrg = document.createElement('script');
      scriptOrg.type = 'application/ld+json';
      scriptOrg.className = 'afcms-global-schema';
      scriptOrg.text = JSON.stringify(organizationJsonLd);
      document.head.appendChild(scriptOrg);

      const scriptWeb = document.createElement('script');
      scriptWeb.type = 'application/ld+json';
      scriptWeb.className = 'afcms-global-schema';
      scriptWeb.text = JSON.stringify(websiteJsonLd);
      document.head.appendChild(scriptWeb);
    }
  }, [settings.title, settings.titleEn, settings.logoUrl, settings.contactPhone, appLanguage]);

  // Dynamic LCP Optimization: Preload the correct Hero image for the active tenant
  React.useEffect(() => {
    if (typeof document !== "undefined") {
      // Remove any existing dynamic preloads
      document.querySelectorAll('.afcms-dynamic-preload').forEach(el => el.remove());

      let preloadUrl = "";
      if (activeTenant === "almas") {
        preloadUrl = settings.homeHeroSlides?.[0] || "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158";
      } else {
        preloadUrl = settings.heroBgUrl || "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158";
      }

      if (preloadUrl) {
        // Use the exact same optimization logic as the components to ensure a cache hit
        const optimizedUrl = getOptimizedImageUrl(preloadUrl, 1400);
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = optimizedUrl;
        link.className = 'afcms-dynamic-preload';
        // @ts-ignore - Priority hint for modern browsers
        link.fetchPriority = 'high';
        document.head.appendChild(link);
      }
    }
  }, [activeTenant, settings.homeHeroSlides, settings.heroBgUrl]);

  // Quote cart state (current active inquiry items)
  const [quoteCart, setQuoteCart] = React.useState<InquiryItem[]>([]);
  const [inquirySuccess, setInquirySuccess] = React.useState(false);

  // Sync Search/Category query to URL for deep linking
  React.useEffect(() => {
    if (typeof window !== "undefined" && appMode !== "admin") {
      const params = new URLSearchParams(window.location.search);
      
      if (searchQuery) {
        params.set("q", searchQuery);
      } else {
        params.delete("q");
      }
      
      // Only sync product categories if we are in store or home modes
      // Blog categories are managed internally by the Blog component
      if (currentTab === "store" || currentTab === "home") {
        if (selectedCategory !== "همه") {
          params.set("cat", selectedCategory);
        } else {
          params.delete("cat");
        }
      }

      let pathname = "/";
      if (currentTab === "store") pathname = "/store";
      else if (currentTab === "blog") pathname = "/blog";
      else if (currentTab === "about") pathname = "/about";
      else if (currentTab === "contact") pathname = "/contact";
      else if (currentTab === "home") pathname = "/";

      // Update URL
      const queryString = params.toString().replace(/\+/g, '%20');
      const newUrl = `${pathname}${queryString ? '?' + queryString : ''}`;
      
      if (window.location.pathname !== pathname) {
        window.history.pushState({ tab: currentTab }, "", newUrl);
      } else {
        window.history.replaceState({ tab: currentTab }, "", newUrl);
      }
    }
  }, [searchQuery, selectedCategory, currentTab, appMode]);

  // حفظ انتخاب سایت در سوپر پنل هنگام رفرش صفحه
  React.useEffect(() => {
    if (appMode === "admin" && typeof sessionStorage !== "undefined") {
      sessionStorage.setItem("afcms_active_tenant", activeTenant);
    }
  }, [activeTenant, appMode]);

  // Dynamic SEO: Update Document Title, Meta Description and Canonical
  React.useEffect(() => {
    if (appMode === "admin") {
      document.title = appLanguage === "en" ? "AFCMS | Central Management" : "AFCMS | سیستم مدیریت افغان";
    } else {
      const isAlmasDomain = typeof window !== 'undefined' && window.location.hostname.includes('almas');
      const displayTitle = appLanguage === "en" ? (settings.titleEn || settings.title) : settings.title;
      const displaySubtitle = appLanguage === "en" ? (settings.subtitleEn || settings.subtitle || 'Premium Catalog') : (settings.subtitle || 'کاتالوگ تخصصی');
      
      let titlePrefix = "";
      if (currentTab === "store") titlePrefix = appLanguage === "en" ? "Catalog | " : "فروشگاه کالا | ";
      else if (currentTab === "blog") titlePrefix = appLanguage === "en" ? `${settings.blogPageTitleEn || "Blog"} | ` : `${settings.blogPageTitle || "وبلاگ"} | `;
      else if (currentTab === "about") titlePrefix = appLanguage === "en" ? "About Us | " : "درباره ما | ";
      else if (currentTab === "contact") titlePrefix = appLanguage === "en" ? "Contact | " : "ارتباط با ما | ";

      document.title = displayTitle ? `${titlePrefix}${displayTitle} | ${displaySubtitle}` : (appLanguage === "en" ? "Loading..." : "در حال بارگذاری...");
      
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription && displaySubtitle) {
        metaDescription.setAttribute("content", displaySubtitle);
      }

      // Update Favicon
      if (settings.faviconUrl) {
        const favicon = document.getElementById('dynamic-favicon') as HTMLLinkElement;
        if (favicon) {
          favicon.href = settings.faviconUrl;
          // Update type based on extension
          if (settings.faviconUrl.endsWith('.png')) favicon.type = 'image/png';
          else if (settings.faviconUrl.endsWith('.ico')) favicon.type = 'image/x-icon';
        }
      }

      // Update Canonical Link
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      const currentUrl = new URL(window.location.href);
      const canonicalUrl = `${currentUrl.origin}${currentUrl.pathname}${currentUrl.search}`;
      canonical.setAttribute("href", canonicalUrl);

      // Update Open Graph Tags
      const updateTag = (name: string, content: string, attr: string = 'property') => {
        let tag = document.querySelector(`meta[${attr}="${name}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute(attr, name);
          document.head.appendChild(tag);
        }
        tag.setAttribute("content", content);
      };

      updateTag("og:title", document.title);
      updateTag("og:description", displaySubtitle || "");
      updateTag("og:url", canonicalUrl);
      if (settings.logoUrl) updateTag("og:image", settings.logoUrl);
    }
  }, [settings.title, settings.subtitle, settings.titleEn, settings.subtitleEn, settings.faviconUrl, appMode, appLanguage, currentTab]);

  React.useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      const params = new URLSearchParams(window.location.search);

      // 1. SEO Redirect Handler: Catch legacy WordPress URLs
      
      // Product Redirects
      const productPaths = ['/product/', '/shop/', '/granules/'];
      if (productPaths.some(p => path.startsWith(p) && path.length > p.length)) {
        const slug = path.split('/').filter(Boolean).pop();
        setCurrentTab("store");
        window.history.replaceState({}, "", `/store?p=${slug}`);
        return;
      }

      // Blog Redirects
      const blogPaths = ['/post/', '/blog/', '/category/'];
      if (blogPaths.some(p => path.startsWith(p) && path.length > p.length)) {
        const parts = path.split('/').filter(Boolean);
        const slug = parts.pop();
        const isCategory = path.includes('/category/');
        setCurrentTab("blog");
        window.history.replaceState({}, "", `/blog?${isCategory ? 'cat' : 'slug'}=${slug}`);
        return;
      }

      // Locale Redirects
      if (path.startsWith('/en_us/') || path.startsWith('/da_af/')) {
         // App language state initialization already handles this, just clean path
         window.history.replaceState({}, "", "/" + window.location.search);
         return;
      }

      if (appMode === "admin") {
        setCurrentTab("admin");
        return;
      }

      // Block admin access on non-admin sites
      if (appMode !== "admin" && (path === "/app" || path === "/admin")) {
        setCurrentTab("404");
        return;
      }

      if (path === "/app" || path === "/admin") {
        setCurrentTab("admin");
      } else if (path === "/store") {
        setCurrentTab("store");
      } else if (path === "/about") {
        setCurrentTab("about");
      } else if (path === "/contact") {
        setCurrentTab("contact");
      } else if (path === "/blog" || (path === "/" && (params.has("slug") || params.has("cat")))) {
        setCurrentTab("blog");
      } else if (path === "/" || path === "") {
        setCurrentTab("home");
      } else {
        setCurrentTab("404");
      }

      setSearchQuery(params.get("q") || "");
      setSelectedCategory(params.get("cat") || "همه");
    };
    window.addEventListener("popstate", handlePopState);
    handlePopState(); // Initial check on mount
    return () => window.removeEventListener("popstate", handlePopState);
  }, [appMode]);

  // Check Supabase Auth Session globally
  React.useEffect(() => {
    if (!settings.supabaseUrl || !settings.supabaseAnonKey) {
      return;
    }
    const client = getSupabaseClient(settings);
    if (client) {
      client.auth.getSession().then(({ data: { session } }) => {
        setIsAdminAuthenticated(!!session);
      });

      const { data: { subscription } } = client.auth.onAuthStateChange((_event, session) => {
        setIsAdminAuthenticated(!!session);
      });

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [settings.supabaseUrl, settings.supabaseAnonKey]);

  // Initialize from LocalStorage or Fallbacks with instant Local load + background cloud sync!
  React.useEffect(() => {
    let isMounted = true;
    try {
      const tSuffix = activeTenant === "default" ? "" : `_${activeTenant}`;
      let hasLocalSettings = false;

      // 1. Core Settings load
      const savedSettings = localStorage.getItem(`sarvarpayman_settings${tSuffix}`);
      let activeSettings = DEFAULT_SETTINGS;
      if (savedSettings) {
        try {
          activeSettings = JSON.parse(savedSettings);
          setSettings(activeSettings);
          hasLocalSettings = true;
        } catch {
          setSettings(DEFAULT_SETTINGS);
        }
      } else {
        setSettings(DEFAULT_SETTINGS);
      }

      // 2. Local Products load
      const savedProducts = localStorage.getItem(`sarvarpayman_products${tSuffix}`);
      if (savedProducts) {
        try {
          const parsed = JSON.parse(savedProducts);
          setProducts(parsed.map((p: any) => ({
            ...p, category: Array.isArray(p.category) ? p.category : [p.category || "بدون دسته‌بندی"]
          })));
        } catch {
          setProducts([]);
        }
      } else {
        setProducts([]);
      }

      // 3. Local Inquiries load
      const savedInquiries = localStorage.getItem(`sarvarpayman_inquiries${tSuffix}`);
      if (savedInquiries) {
        try {
          setInquiries(JSON.parse(savedInquiries));
        } catch {
          setInquiries([]);
        }
      } else {
        setInquiries([]);
      }

      // 4. Local Posts load
      const savedPosts = localStorage.getItem(`sarvarpayman_posts${tSuffix}`);
      if (savedPosts) {
        try {
          setPosts(JSON.parse(savedPosts));
        } catch {
          setPosts([]);
        }
      } else {
        setPosts([]);
      }

      if (hasLocalSettings) {
        setIsAppLoading(false);
      } else {
        setIsAppLoading(true);
      }

      // 5. Background Supabase Cloud Connection & Synchronisation
      const loadBackgroundCloudData = async () => {
        try {
          // Fetch EVERYTHING in parallel to eliminate waterfall delay
          const [cloudSettings, cloudProducts, cloudInquiries, cloudPosts] = await Promise.all([
            dbFetchSiteSettings(activeSettings, activeTenant),
            dbFetchProducts(activeSettings, [], activeTenant),
            dbFetchInquiries(activeSettings, activeTenant),
            dbFetchPosts(activeSettings, activeTenant)
          ]);

          if (cloudSettings) {
            activeSettings = { 
              ...activeSettings, 
              ...cloudSettings, 
              supabaseUrl: activeSettings.supabaseUrl, supabaseAnonKey: activeSettings.supabaseAnonKey, 
              wooConsumerKey: activeSettings.wooConsumerKey, wooConsumerSecret: activeSettings.wooConsumerSecret, wpUrl: activeSettings.wpUrl 
            };
            if (isMounted) setSettings(activeSettings);
            localStorage.setItem(`sarvarpayman_settings${tSuffix}`, JSON.stringify(activeSettings));
          }

          if (isMounted) {
            if (cloudProducts) setProducts(cloudProducts);
            if (cloudInquiries) setInquiries(cloudInquiries);
            if (cloudPosts) setPosts(cloudPosts);
            
            localStorage.setItem(`sarvarpayman_products${tSuffix}`, JSON.stringify(cloudProducts || []));
            localStorage.setItem(`sarvarpayman_inquiries${tSuffix}`, JSON.stringify(cloudInquiries || []));
            localStorage.setItem(`sarvarpayman_posts${tSuffix}`, JSON.stringify(cloudPosts || []));
          }
        } catch (err) {
          console.log("Offline or no Cloud database connected. Continuing using sandboxed LocalStorage.");
        } finally {
          if (isMounted && !hasLocalSettings) {
            setIsAppLoading(false);
          }
        }
      };
      
      loadBackgroundCloudData();
    } catch (e) {
      console.error("Local storage sync loading failed", e);
      if (isMounted) {
        setProducts([]);
        setSettings(DEFAULT_SETTINGS);
        setIsAppLoading(false);
      }
    }

    return () => { isMounted = false; };
  }, [activeTenant]);

  // Helper persistence wrappers
  const saveProductsToDb = (newList: Product[]) => {
    setProducts(newList);
    const tSuffix = activeTenant === "default" ? "" : `_${activeTenant}`;
    localStorage.setItem(`sarvarpayman_products${tSuffix}`, JSON.stringify(newList));
  };

  const saveInquiriesToDb = (newList: Inquiry[]) => {
    setInquiries(newList);
    const tSuffix = activeTenant === "default" ? "" : `_${activeTenant}`;
    localStorage.setItem(`sarvarpayman_inquiries${tSuffix}`, JSON.stringify(newList));
  };

  const savePostsToDb = (newList: Post[]) => {
    setPosts(newList);
    const tSuffix = activeTenant === "default" ? "" : `_${activeTenant}`;
    localStorage.setItem(`sarvarpayman_posts${tSuffix}`, JSON.stringify(newList));
  };

  const handleUpsertPost = async (post: Post) => {
    const success = await dbUpsertPost(settings, post);
    if (success) {
      setPosts(prev => {
        const exists = prev.find(p => p.id === post.id);
        const newList = exists ? prev.map(p => p.id === post.id ? post : p) : [post, ...prev];
        const tSuffix = activeTenant === "default" ? "" : `_${activeTenant}`;
        localStorage.setItem(`sarvarpayman_posts${tSuffix}`, JSON.stringify(newList));
        return newList;
      });
    }
  };

  const handleDeletePost = async (id: string) => {
    const success = await dbDeletePost(settings, id);
    if (success) {
      setPosts(prev => {
        const newList = prev.filter(p => p.id !== id);
        const tSuffix = activeTenant === "default" ? "" : `_${activeTenant}`;
        localStorage.setItem(`sarvarpayman_posts${tSuffix}`, JSON.stringify(newList));
        return newList;
      });
    }
  };

  const saveSettingsToDb = async (newSettings: SiteSettings) => {
    setSettings(newSettings);
    const tSuffix = activeTenant === "default" ? "" : `_${activeTenant}`;
    localStorage.setItem(`sarvarpayman_settings${tSuffix}`, JSON.stringify(newSettings));

    try {
      await dbUpsertSiteSettings(newSettings, newSettings, activeTenant);
    } catch (err) {
      console.warn("Could not sync settings to cloud", err);
    }

    // Refetch instantly when settings (like Supabase API key configuration) are customized
    try {
      const cloudProducts = await dbFetchProducts(newSettings, [], activeTenant);
      setProducts(cloudProducts);
      localStorage.setItem(`sarvarpayman_products${tSuffix}`, JSON.stringify(cloudProducts));

      const cloudInquiries = await dbFetchInquiries(newSettings, activeTenant);
      setInquiries(cloudInquiries);
      localStorage.setItem(`sarvarpayman_inquiries${tSuffix}`, JSON.stringify(cloudInquiries));
    } catch (err) {
      console.log("Supabase dynamically initialized, but connection testing failed. Keeping sandboxed data.");
    }
  };

  const handleLogout = async () => {
    if (settings.supabaseUrl && settings.supabaseAnonKey) {
      const client = getSupabaseClient(settings);
      if (client) {
        await client.auth.signOut();
      }
    }
    setIsAdminAuthenticated(false);
    if (appMode !== "admin") {
      setCurrentTab("home");
    }
  };

  // Cart operations
  const handleAddToQuote = (product: Product) => {
    const exists = quoteCart.some((item) => item.id === product.id);
    if (exists) return;

    const cartItem: InquiryItem = {
      id: product.id,
      name: product.name,
      sku: product.sku,
      category: Array.isArray(product.category) ? product.category.join("، ") : product.category,
      price: product.price,
      quantity: 1,
      unit: product.unit || product.specs?.packing || "کیسه ۲۵ کیلوگرم",
    };

    setQuoteCart([...quoteCart, cartItem]);
  };

  const handleRemoveFromCart = (id: string) => {
    setQuoteCart(quoteCart.filter((item) => item.id !== id));
  };

  const handleUpdateCartItemNote = (id: string, customNote: string) => {
    setQuoteCart(quoteCart.map((item) => (item.id === id ? { ...item, customNote } : item)));
  };

  const handleUpdateCartItemQuantity = (id: string, newQuantity: number) => {
    if (newQuantity < 1) return; // جلوگیری از صفر یا منفی شدن تعداد
    setQuoteCart(quoteCart.map((item) => (item.id === id ? { ...item, quantity: newQuantity } : item)));
  };

  // Final Quote Submission
  const handleSubmitQuote = async (customerData: { name: string; phone: string; company: string; note: string }) => {
    const total = quoteCart.reduce((acc, item) => acc + (item.price * item.quantity), 0);

    const newInquiry: Inquiry = {
      id: `inq-${Date.now()}`,
      customerName: customerData.name,
      phone: customerData.phone,
      company: customerData.company,
      message: customerData.note,
      createdAt: new Date().toISOString(),
      items: [...quoteCart],
      status: "new",
      totalPrice: total,
    };

    const updatedInquiries = [newInquiry, ...inquiries];
    saveInquiriesToDb(updatedInquiries);

    // Sync Submission to Supabase Cloud, if available
    try {
      await dbInsertInquiry(settings, newInquiry, activeTenant);
    } catch {
      console.warn("Could not sync inquiry with Cloud DB database. Kept safe in Local Storage.");
    }

    setQuoteCart([]);
    setInquirySuccess(true);
    setTimeout(() => setInquirySuccess(false), 5000);
  };

  // CLIENT CRUD (Called from CMS / Importer)
  const handleImportProducts = async (newProducts: Product[]) => {
    // Append but avoid duplicate SKUs
    const filteredNew = newProducts.filter(
      (n) => !products.some((p) => p.sku === n.sku)
    );
    const updated = [...filteredNew, ...products];
    saveProductsToDb(updated);

    // Sync each newly imported product with Supabase API
    try {
      for (const prod of filteredNew) {
        await dbUpsertProduct(settings, prod, activeTenant);
      }
    } catch (err) {
      console.warn("WordPress migration succeeded locally. Cloud sync failed:", err);
    }

    if (appMode !== "admin") {
      setCurrentTab("store"); // Navigate back to the main search view to inspect imports!
    }
  };

  const handleImportPosts = async (newPosts: Post[]) => {
    const updated = [...newPosts, ...posts];
    savePostsToDb(updated);

    try {
      for (const post of newPosts) {
        await dbUpsertPost(settings, post);
      }
    } catch (err) {
      console.warn("Post migration failed:", err);
    }
  };

  const handleAddProduct = async (prod: Product) => {
    const updated = [prod, ...products];
    saveProductsToDb(updated);

    // Live Cloud Sync
    try {
      await dbUpsertProduct(settings, prod, activeTenant);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditProduct = async (edited: Product) => {
    const updated = products.map((p) => (p.id === edited.id ? edited : p));
    saveProductsToDb(updated);

    // Live Cloud Sync
    try {
      await dbUpsertProduct(settings, edited, activeTenant);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteProduct = async (id: string) => {
    const updated = products.filter((p) => p.id !== id);
    saveProductsToDb(updated);

    // Live Cloud Sync
    try {
      await dbDeleteProduct(settings, id); // id is unique globally across tenants
    } catch (err) {
      console.error(err);
    }
  };

  const handleReorderProducts = (reordered: Product[]) => {
    saveProductsToDb(reordered);
    // نکته: برای ذخیره دائمی این ترتیب در دیتابیس ابری سوپابیس نیاز به اضافه کردن ستون `sort_order` است، اما هم‌اکنون در حافظه سیستم اعمال می‌شود.
  };

  const handleBulkDeleteProducts = async (ids: string[]) => {
    const updated = products.filter((p) => !ids.includes(p.id));
    saveProductsToDb(updated);

    try {
      await dbBulkDeleteProducts(settings, ids);
    } catch (err) {
      console.error(err);
    }
  };

  const handleBulkUpdateProducts = async (ids: string[], updates: Partial<Product>, specUpdates?: any) => {
    const updatedProducts = products.map(p => {
      if (ids.includes(p.id)) {
        return { ...p, ...updates, specs: specUpdates ? { ...p.specs, ...specUpdates } : p.specs };
      }
      return p;
    });
    saveProductsToDb(updatedProducts);

    const productsToUpsert = updatedProducts.filter(p => ids.includes(p.id));
    try {
      await dbBulkUpsertProducts(settings, productsToUpsert, activeTenant);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteInquiry = async (id: string) => {
    const updated = inquiries.filter((inq) => inq.id !== id);
    saveInquiriesToDb(updated);

    // Live Cloud sync delete
    try {
      await dbDeleteInquiry(settings, id);
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleInquiryStatus = async (id: string) => {
    // Mark as viewed/completed
    const updated = inquiries.map((inq) =>
      inq.id === id ? { ...inq, status: "completed" as const } : inq
    );
    saveInquiriesToDb(updated);

    // Live Cloud sync status update
    try {
      await dbUpdateInquiryStatus(settings, id, "completed");
    } catch (err) {
      console.error(err);
    }
  };

  if (isAppLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-slate-500 space-y-4" style={{ direction: "rtl" }}>
        <Loader className="w-8 h-8 text-indigo-600 animate-spin" />
        <p className="text-xs font-bold animate-pulse">در حال برقراری ارتباط و همگام‌سازی اطلاعات...</p>
      </div>
    );
  }

  const isAlmas = activeTenant === "almas" && appMode !== "admin";

  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": appLanguage === "en" ? (settings.titleEn || settings.title) : settings.title,
    "url": window.location.origin,
    "logo": settings.logoUrl,
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": settings.contactPhone,
      "contactType": "customer service"
    }
  };

  const websiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": appLanguage === "en" ? (settings.titleEn || settings.title) : settings.title,
    "url": window.location.origin,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${window.location.origin}/store?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900 font-sans selection:bg-indigo-500 selection:text-white" dir={appLanguage === "fa" ? "rtl" : "ltr"}>
      {/* Universal header navigation */}
      <Header
        currentTab={currentTab}
        setCurrentTab={(tab) => {
          setCurrentTab(tab);
          // Auto scroll to top on tab changes
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        settings={settings}
        quoteCart={quoteCart}
        removeFromCart={handleRemoveFromCart}
        updateCartItemNote={handleUpdateCartItemNote}
        updateCartItemQuantity={handleUpdateCartItemQuantity}
        onSubmitQuote={handleSubmitQuote}
        inquirySuccess={inquirySuccess}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isAuthenticated={isAdminAuthenticated}
        onLogout={handleLogout}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedOrigin={selectedOrigin}
        setSelectedOrigin={setSelectedOrigin}
        sortBy={sortBy}
        setSortBy={setSortBy}
        appLanguage={appLanguage}
        setAppLanguage={setAppLanguage}
      />

      {/* Hero section specifically shown at Catalog to initiate queries */}
      {currentTab === "home" && !isAlmas && (
        <Hero
          settings={settings}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setCurrentTab={setCurrentTab}
          products={products}
          appLanguage={appLanguage}
        />
      )}

      {/* Main Container flex-grow to push footer down */}
      <main className="w-full flex-grow">
        {currentTab === "home" && isAlmas && (
          <AlmasLanding
            setCurrentTab={setCurrentTab}
            settings={settings}
            products={products}
            posts={posts}
            currency={settings.currency || "تومان"}
            onAddToQuote={handleAddToQuote}
            quoteCart={quoteCart}
            setSelectedCategory={setSelectedCategory}
            appLanguage={appLanguage}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
        )}
        {(currentTab === "store" || (currentTab === "home" && !isAlmas)) && (
          <Catalog
            isLandingMode={currentTab === "home"}
            setCurrentTab={setCurrentTab}
            settings={settings}
            products={products}
            currency={settings.currency || "تومان"}
            onAddToQuote={handleAddToQuote}
            quoteCart={quoteCart}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedOrigin={selectedOrigin}
            setSelectedOrigin={setSelectedOrigin}
            sortBy={sortBy}
            setSortBy={setSortBy}
            appLanguage={appLanguage}
          />
        )}
        
        {currentTab === "about" && (
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader className="w-8 h-8 text-indigo-600 animate-spin" /></div>}>
            <About 
              settings={settings} 
              appLanguage={appLanguage} 
            />
          </React.Suspense>
        )}

        {currentTab === "contact" && (
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader className="w-8 h-8 text-indigo-600 animate-spin" /></div>}>
            <Contact 
              settings={settings} 
              appLanguage={appLanguage} 
            />
          </React.Suspense>
        )}

        {currentTab === "blog" && (
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader className="w-8 h-8 text-indigo-600 animate-spin" /></div>}>
            <Blog
              posts={posts}
              settings={settings}
              appLanguage={appLanguage}
            />
          </React.Suspense>
        )}

        {currentTab === "admin" && (
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader className="w-8 h-8 text-indigo-600 animate-spin" /></div>}>
            <AdminPanel
              products={products}
              inquiries={inquiries}
              posts={posts}
              settings={settings}
              onUpdateSettings={saveSettingsToDb}
              onAddProduct={handleAddProduct}
              onEditProduct={handleEditProduct}
              onDeleteProduct={handleDeleteProduct}
              onAddPost={handleUpsertPost}
              onEditPost={handleUpsertPost}
              onDeletePost={handleDeletePost}
              onReorderProducts={handleReorderProducts}
              onBulkUpdateProducts={handleBulkUpdateProducts}
              onBulkDeleteProducts={handleBulkDeleteProducts}
              onDeleteInquiry={handleDeleteInquiry}
              onToggleInquiryStatus={handleToggleInquiryStatus}
              onImportProducts={handleImportProducts}
              onImportPosts={handleImportPosts}
              isAuthenticated={isAdminAuthenticated}
              setIsAuthenticated={setIsAdminAuthenticated}
              onLogout={handleLogout}
              activeTenant={activeTenant}
              setActiveTenant={setActiveTenant}
              appLanguage={appLanguage}
            />
          </React.Suspense>
        )}

        {currentTab === "404" && (
          <div className="flex flex-col items-center justify-center py-24 md:py-32 px-4 text-center animate-fadeIn" style={{ direction: "rtl" }}>
            <div className="w-24 h-24 bg-rose-50 text-rose-500 rounded-3xl flex items-center justify-center mb-6 shadow-sm border border-rose-100 rotate-12 hover:rotate-0 transition-transform duration-300">
              <AlertCircle className="w-10 h-10" />
            </div>
            <h2 className="text-6xl md:text-8xl font-black text-slate-900 mb-4 tracking-tighter drop-shadow-sm">۴۰۴</h2>
            <h3 className="text-lg md:text-xl font-bold text-slate-700 mb-3">صفحه‌ای که به دنبال آن هستید پیدا نشد!</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto mb-8 leading-relaxed">
              احتمالاً آدرس را اشتباه وارد کرده‌اید یا دسترسی به این بخش برای شما محدود شده است. لطفاً برای ادامه به صفحه اصلی وب‌سایت برگردید.
            </p>
            <button
              onClick={() => setCurrentTab("home")}
              className="px-6 py-3.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-sm font-bold transition-all shadow-md flex items-center gap-2 cursor-pointer"
            >
              <Home className="w-4 h-4" />
              <span>بازگشت به صفحه اصلی</span>
            </button>
          </div>
        )}
      </main>

      {/* Refined and Separated Footer */}
      <footer className={`w-full mt-auto ${appMode === "admin" ? "bg-[#0f172a] border-t border-slate-800" : isAlmas ? "bg-[#060d1a] border-t border-blue-900/40" : "bg-slate-900 border-t border-slate-800"}`} dir={appLanguage === "fa" ? "rtl" : "ltr"}>
        <div className="max-w-7xl mx-auto px-4 py-10 md:py-14">
          
          {appMode === "admin" ? (
            /* ADMIN SPECIFIC FOOTER LAYOUT */
            <div className="flex flex-col items-center text-center space-y-8 animate-fadeIn">
              <div className="flex flex-col items-center gap-4">
                <div className="bg-white p-4 rounded-[2rem] border border-slate-200 shadow-xl">
                  <img src="https://osymbnivgxwtxymgzvre.supabase.co/storage/v1/object/public/platform-assets/1777879491006.png" alt="AFCMS Logo" className="h-12 md:h-14 w-auto object-contain" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-base font-black text-white tracking-widest uppercase">AFCMS CENTRAL MANAGEMENT</h3>
                  <p className="text-[10px] text-slate-500 font-bold tracking-[0.3em]">VERSION 4.2.0 EDGE</p>
                </div>
              </div>
              <p className="text-xs text-slate-300 max-w-lg leading-loose font-medium opacity-90">
                هسته مرکزی مدیریت و مانیتورینگ چندسایته هلدینگ‌های تجاری و کاتالوگ‌های فروشگاهی. این پلتفرم تحت پروتکل‌های امنیتی AFCMS محافظت می‌شود.
              </p>
            </div>
          ) : (
            /* CLIENT SPECIFIC FOOTER LAYOUT */
            <div className="flex flex-col md:flex-row justify-between items-start gap-12 md:gap-8">
              <div className="text-center md:text-start space-y-4 max-w-lg">
                {settings.logoUrl && (
                  <div className="bg-white p-3 md:p-3.5 rounded-2xl shadow-md border border-slate-200 inline-block mb-4 mx-auto md:mx-0">
                    <img src={getOptimizedImageUrl(settings.logoUrl, 120)} alt="لوگوی سیستم" width="160" height="64" className="h-14 md:h-16 w-auto object-contain transition-transform hover:scale-105" referrerPolicy="no-referrer" />
                  </div>
                )}
                <h3 className="text-base md:text-lg font-black text-white" dir={appLanguage === "en" ? "ltr" : "rtl"}>
                  {appLanguage === "en" ? (settings.titleEn || settings.title) : settings.title}
                </h3>
                <p className="text-xs text-slate-300 max-w-md leading-relaxed font-medium" dir={appLanguage === "en" ? "ltr" : "rtl"}>
                  {appLanguage === "en" 
                    ? (settings.subtitleEn || (isAlmas ? "Pioneers in importing industrial machinery, mining equipment, and advanced floor polishing lines." : "Specialized reference for mineral pigments, powder colors, and abrasive segments with authenticity guarantee."))
                    : (settings.subtitle || (isAlmas ? "شرکت تجارتی الماس سروری؛ پیشگام در واردات ماشین‌آلات صنعتی، تجهیزات معدن و خطوط پیشرفته کفسابی و سنگبری." : "مرجع تخصصی تامین و توزیع پیگمنت‌های معدنی، رنگ‌های پودری و لقمه‌های کفسابی با تضمین اصالت کالا و مشاوره فنی پیش از خرید."))}
                </p>
              </div>
              
              {settings.footerLinks && settings.footerLinks.length > 0 && (
                <div className="flex flex-col space-y-4 min-w-[200px]">
                  <h4 className={`text-[11px] font-black uppercase tracking-[0.2em] mb-2 ${isAlmas ? 'text-blue-500' : 'text-indigo-500'}`}>
                    {appLanguage === "en" ? "Quick Access" : "دسترسی سریع"}
                  </h4>
                  <div className="grid grid-cols-1 gap-y-3" dir={appLanguage === "en" ? "ltr" : "rtl"}>
                    {settings.footerLinks.map((link) => {
                      const isExternal = link.url.startsWith('http');
                      return (
                        <a 
                          key={link.id} 
                          href={link.url} 
                          target={isExternal ? "_blank" : undefined}
                          rel={isExternal ? "noopener noreferrer" : undefined}
                          className={`text-slate-300 text-xs font-bold flex items-center gap-2.5 group transition-all hover:translate-x-1 ${isAlmas ? "hover:text-cyan-400" : "hover:text-indigo-400"}`}
                          onClick={(e) => {
                            if (!isExternal) {
                              e.preventDefault();
                              const tab = link.url.replace('/', '') || 'home';
                              setCurrentTab(tab);
                              window.scrollTo({ top: 0, behavior: "smooth" });
                            }
                          }}
                        >
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isAlmas ? "bg-blue-500/10 group-hover:bg-cyan-500/20" : "bg-indigo-500/10 group-hover:bg-indigo-500/20"}`}>
                            {getFooterIcon(link.icon)}
                          </div>
                          <span>{appLanguage === "en" ? (link.labelEn || link.label) : link.label}</span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SHARED BOTTOM BAR (AFCMS CREDITS) */}
          <div className="border-t border-slate-800/60 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-6 text-[11px] text-white font-bold uppercase tracking-wider">
            <p className="text-center md:text-start" dir={appLanguage === "en" ? "ltr" : "rtl"}>
              © {new Date().getFullYear()} – {appLanguage === "fa" ? `تمامی حقوق برای ${appMode === "admin" ? "پلتفرم مدیریت AFCMS" : settings.title} محفوظ است.` : `ALL RIGHTS RESERVED BY ${appMode === "admin" ? "AFCMS MANAGEMENT PLATFORM" : (settings.titleEn || settings.title)}`}
            </p>
            
            <a href="https://afcms.tech" target="_blank" rel="noopener noreferrer" title="Running on AFCMS Enterprise Infrastructure" className="flex items-center gap-3 px-5 py-2.5 bg-white hover:bg-slate-50 text-slate-800 rounded-2xl border border-slate-200 shadow-xl transition-all group active:scale-95">
              <span className="text-[10px] font-black text-slate-600 group-hover:text-slate-800 transition-colors">{appLanguage === "en" ? "POWERED BY" : "قدرت گرفته با افتخار از"}</span>
              <img src="https://osymbnivgxwtxymgzvre.supabase.co/storage/v1/object/public/platform-assets/1777879491006.png" alt="AFCMS Logo" width="120" height="40" className="h-5 md:h-6 w-auto object-contain transition-transform group-hover:scale-110" />
              <span className={`font-black tracking-tighter text-sm ${isAlmas ? "text-cyan-600" : "text-indigo-600"}`}>AFCMS.tech</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
